// frontend/src/hooks/useAudioDevices.js
// Enumerates audio input devices and persists the user's selection.
// IMPORTANT: Does NOT open any preliminary getUserMedia — that would
// interfere with the recording stream in Chrome.
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'preferred_audio_device';

export function useAudioDevices() {
  const [devices, setDevices]   = useState([]);
  const [deviceId, setDeviceId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ''
  );
  const [loading, setLoading] = useState(true);

  // Enumerate devices — modern Chrome returns labels once mic permission
  // has been granted to the site (no temp stream needed).
  const refreshDevices = useCallback(async () => {
    try {
      setLoading(true);
      const all  = await navigator.mediaDevices.enumerateDevices();
      const mics = all.filter(d => d.kind === 'audioinput');

      // If labels are empty the user hasn't granted permission yet —
      // we'll retry automatically after the first real getUserMedia succeeds.
      setDevices(mics);

      // If the saved device disappeared, reset to default
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && !mics.find(d => d.deviceId === saved)) {
        setDeviceId('');
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.warn('useAudioDevices: enumerateDevices error', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshDevices();
    navigator.mediaDevices.addEventListener('devicechange', refreshDevices);
    return () => navigator.mediaDevices.removeEventListener('devicechange', refreshDevices);
  }, [refreshDevices]);

  const selectDevice = useCallback((id) => {
    setDeviceId(id);
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Build getUserMedia constraints.
  // Always include audio-processing flags explicitly so Chrome keeps
  // echo-cancellation / noise-suppression / AGC even when a specific
  // deviceId is set (omitting them causes Chrome to disable processing).
  const getAudioConstraints = useCallback(() => {
    const processing = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl:  true,
      sampleRate:       16000,   // hint — browser may resample if unsupported
    };

    if (!deviceId) {
      return { audio: processing };
    }

    return {
      audio: {
        ...processing,
        deviceId: { exact: deviceId },
      }
    };
  }, [deviceId]);

  // After a successful recording getUserMedia we re-enumerate so labels
  // appear in the picker (call this once mic permission is confirmed).
  const refreshAfterPermission = useCallback(() => {
    refreshDevices();
  }, [refreshDevices]);

  const selectedDevice = devices.find(d => d.deviceId === deviceId) || null;

  return {
    devices,
    deviceId,
    selectedDevice,
    selectDevice,
    getAudioConstraints,
    refreshAfterPermission,
    loading,
  };
}
